import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, UtensilsCrossed, Plus, Trash2, Printer, Search, X,
  CreditCard, RefreshCw, Unlock, ChefHat, Send, Check, Clock, Settings, ConciergeBell, Ban, LogOut, Maximize2,
} from "lucide-react";
import ProductoSearchSelect from "../components/ProductoSearchSelect";

const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all font-body disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-all border border-white/10";
const INPUT = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";

const fmtGs = (n) => `₲ ${Math.round(Number(n) || 0).toLocaleString("es-PY")}`;

function imprimirTarjeta(tarjeta, empresaEmisora) {
  const w = window.open("", "_blank");
  if (!w) { toast.error("El navegador bloqueó la ventana de impresión"); return; }
  const nombre = empresaEmisora?.razon_social || empresaEmisora?.nombre || "Arandu&JAR Informática";
  const html = `
    <html><head><title>Tarjeta N° ${tarjeta.numero}</title>
    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
    <style>
      body { font-family: Arial, sans-serif; text-align: center; padding: 30px; }
      h1 { font-size: 15px; color: #555; margin: 0 0 4px; }
      h2 { font-size: 28px; margin: 6px 0 14px; }
      svg { margin: 0 auto; display: block; }
    </style></head>
    <body>
      <h1>${nombre}</h1>
      <h2>Mesa / Tarjeta N° ${tarjeta.numero}</h2>
      <svg id="bc"></svg>
      <script>
        JsBarcode("#bc", "${tarjeta.codigo_barras}", { format: "code128", height: 60, displayValue: true });
        window.onload = () => setTimeout(() => window.print(), 300);
      </script>
    </body></html>`;
  w.document.write(html);
  w.document.close();
}

const COLOR_ESTADO = {
  pendiente:  { card: "bg-red-500/15 border-red-500/60",     badge: "bg-red-500/20 text-red-300 border-red-500/40",       btn: "bg-red-600 hover:bg-red-500" },
  en_proceso: { card: "bg-amber-500/15 border-amber-500/60", badge: "bg-amber-500/20 text-amber-300 border-amber-500/40", btn: "bg-amber-600 hover:bg-amber-500" },
  terminado:  { card: "bg-emerald-500/15 border-emerald-500/60", badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", btn: "bg-emerald-600 hover:bg-emerald-500" },
  entregado:  { card: "bg-slate-500/10 border-slate-500/40", badge: "bg-slate-500/20 text-slate-300 border-slate-500/40", btn: "bg-slate-600" },
};
const LABEL_ESTADO = { pendiente: "Pedido", en_proceso: "En proceso", terminado: "Terminado", entregado: "Entregado" };

const minutosDesde = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
};

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; g.gain.value = 0.15;
    o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 250);
  } catch (e) { /* sin audio */ }
}

// Pantalla de pedidos por mesa: vista "cocina" (pendiente / en proceso) o "mozos" (incluye terminados → entregado).
function PedidosGrid({ vista, API, headers, jsonHeaders, logoTipo }) {
  const [pedidos, setPedidos] = useState([]);
  const [expandido, setExpandido] = useState(null);   // pedido_id
  const [, setTick] = useState(0);
  const [cargado, setCargado] = useState(false);
  const previos = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/restaurante/pedidos?vista=${vista}&logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
      if (!r.ok) return;
      const d = await r.json();
      // sonido: cocina avisa pedidos nuevos; mozos avisa pedidos recién terminados
      const clave = vista === "cocina" ? "pendiente" : "terminado";
      const nuevos = new Set(d.filter(p => p.estado === clave).map(p => p.pedido_id));
      if (previos.current && [...nuevos].some(id => !previos.current.has(id))) beep();
      previos.current = nuevos;
      setPedidos(d); setCargado(true);
    } catch (e) { /* reintenta en el próximo ciclo */ }
  }, [API, vista, logoTipo]); // eslint-disable-line

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, 5000);
    const k = setInterval(() => setTick(x => x + 1), 30000);
    return () => { clearInterval(t); clearInterval(k); };
  }, [cargar]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") setExpandido(null); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const accion = (p) => {
    if (vista === "cocina") {
      if (p.estado === "pendiente") return { sig: "en_proceso", texto: "Empezar" };
      if (p.estado === "en_proceso") return { sig: "terminado", texto: "Terminado" };
    } else if (p.estado === "terminado") return { sig: "entregado", texto: "Entregado" };
    return null;
  };

  const avanzar = async (p, sig) => {
    try {
      const r = await fetch(`${API}/admin/restaurante/comandas/${p.comanda_id}/pedidos/${p.pedido_id}/estado`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ estado: sig }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "No se pudo cambiar el estado");
      setExpandido(null);
      cargar();
    } catch (e) { toast.error(e.message); }
  };

  const n = pedidos.length;
  const cols = n <= 1 ? 1 : n <= 4 ? 2 : n <= 9 ? 3 : n <= 16 ? 4 : n <= 25 ? 5 : 6;
  const sel = pedidos.find(p => p.pedido_id === expandido);

  const Contenido = ({ p, grande }) => {
    const a = accion(p);
    const col = COLOR_ESTADO[p.estado];
    return (
      <>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className={`font-bold text-white ${grande ? "text-4xl" : "text-2xl"}`}>Mesa {p.mesa_numero}{p.mesa_nombre ? <span className="text-slate-300 text-base font-normal"> · {p.mesa_nombre}</span> : null}</div>
            <div className="text-slate-300 text-sm mt-0.5">Pedido #{p.numero}{p.mozo ? ` · ${p.mozo}` : ""}</div>
          </div>
          <div className="text-right shrink-0">
            <span className={`text-xs px-2 py-1 rounded-full border ${col.badge}`}>{LABEL_ESTADO[p.estado]}</span>
            <div className="text-slate-300 text-sm mt-1 flex items-center gap-1 justify-end"><Clock className="w-3.5 h-3.5" />{minutosDesde(p.enviado_en)} min</div>
          </div>
        </div>
        <ul className={`mt-3 space-y-1 overflow-y-auto flex-1 ${grande ? "text-2xl" : "text-lg"}`}>
          {p.items.map((it, i) => (
            <li key={i} className="text-white"><span className="font-bold text-orange-300">{it.cantidad}×</span> {it.nombre}</li>
          ))}
        </ul>
        {a && (
          <button onClick={(e) => { e.stopPropagation(); avanzar(p, a.sig); }}
            className={`mt-3 w-full py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2 ${COLOR_ESTADO[a.sig].btn} ${grande ? "text-xl" : "text-base"}`}>
            <Check className="w-5 h-5" /> {a.texto}
          </button>
        )}
      </>
    );
  };

  if (!cargado) return <div className="text-slate-500 text-center py-16 font-body">Cargando pedidos…</div>;
  if (n === 0) return (
    <div className="text-center py-24 text-slate-500 font-body">
      {vista === "cocina" ? <ChefHat className="w-14 h-14 mx-auto mb-3 opacity-40" /> : <ConciergeBell className="w-14 h-14 mx-auto mb-3 opacity-40" />}
      <p className="text-xl">{vista === "cocina" ? "No hay pedidos pendientes en cocina" : "No hay pedidos para entregar"}</p>
    </div>
  );

  return (
    <>
      <div className="grid gap-3 overflow-y-auto" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: "minmax(200px, 1fr)", height: "calc(100vh - 190px)" }}>
        {pedidos.map(p => (
          <div key={p.pedido_id} onClick={() => setExpandido(p.pedido_id)}
            className={`relative border-2 rounded-xl p-4 flex flex-col cursor-pointer min-h-0 ${COLOR_ESTADO[p.estado].card}`}>
            <Maximize2 className="w-3.5 h-3.5 text-slate-500 absolute bottom-2 right-2" />
            <Contenido p={p} />
          </div>
        ))}
      </div>
      {sel && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6">
          <div className={`relative w-full max-w-3xl max-h-[90vh] border-2 rounded-2xl p-8 flex flex-col bg-arandu-dark ${COLOR_ESTADO[sel.estado].card}`}>
            <button onClick={() => setExpandido(null)} className="absolute top-3 right-3 text-slate-300 hover:text-white p-2"><X className="w-7 h-7" /></button>
            <Contenido p={sel} grande />
          </div>
        </div>
      )}
    </>
  );
}

// Sección limpia de disponibilidad: cocina marca los platos agotados; el mozo no puede cargarlos.
function Disponibilidad({ API, headers, jsonHeaders, logoTipo, menu, fetchMenu, canDisp }) {
  const [q, setQ] = useState("");
  const alternar = async (m) => {
    if (!canDisp) return;
    try {
      const r = await fetch(`${API}/admin/restaurante/menu/${m.id}?logo_tipo=${encodeURIComponent(logoTipo)}`, { method: "PUT", headers: jsonHeaders, body: JSON.stringify({ disponible: m.disponible === false }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "No se pudo actualizar");
      fetchMenu();
    } catch (e) { toast.error(e.message); }
  };
  const lista = menu.filter(m => !q || (m.nombre || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-md"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" />
          <input className={`${INPUT} pl-9`} placeholder="Buscar plato…" value={q} onChange={e => setQ(e.target.value)} /></div>
        <span className="text-sm text-slate-400">{menu.filter(m => m.disponible === false).length} agotado/s</span>
      </div>
      {menu.length === 0 && <p className="text-slate-500 font-body">El menú está vacío. Agregá productos en Configuración.</p>}
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}>
        {lista.map(m => {
          const ok = m.disponible !== false;
          return (
            <button key={m.id} onClick={() => alternar(m)} disabled={!canDisp}
              className={`border-2 rounded-xl p-4 text-left transition-all ${ok ? "bg-emerald-500/10 border-emerald-500/40 hover:bg-emerald-500/20" : "bg-red-500/15 border-red-500/60 hover:bg-red-500/25"}`}>
              <div className="text-white font-semibold">{m.nombre}</div>
              <div className={`mt-2 text-sm font-bold ${ok ? "text-emerald-300" : "text-red-300"}`}>{ok ? "Disponible" : "AGOTADO"}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Configuración: categoría del menú (por defecto "Servicios": sin stock) y productos que forman el menú de cocina.
function ConfigMenu({ API, headers, jsonHeaders, logoTipo, menu, fetchMenu }) {
  const [cat, setCat] = useState("Servicios");
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [soloCat, setSoloCat] = useState(true);
  const [cands, setCands] = useState([]);
  const lq = encodeURIComponent(logoTipo);

  useEffect(() => {
    fetch(`${API}/admin/restaurante/config?logo_tipo=${lq}`, { headers }).then(r => r.ok ? r.json() : null).then(d => d && setCat(d.categoria_menu)).catch(() => {});
    fetch(`${API}/admin/categorias-productos?logo_tipo=${lq}`, { headers }).then(r => r.ok ? r.json() : []).then(d => setCats((Array.isArray(d) ? d : []).map(c => c.nombre).filter(Boolean))).catch(() => {});
  }, [API, logoTipo]); // eslint-disable-line

  const buscar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/restaurante/menu/candidatos?logo_tipo=${lq}&solo_categoria=${soloCat}&q=${encodeURIComponent(q)}`, { headers });
      setCands(r.ok ? await r.json() : []);
    } catch (e) { setCands([]); }
  }, [API, lq, soloCat, q]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(buscar, 250); return () => clearTimeout(t); }, [buscar, menu.length, cat]);

  const guardarCat = async () => {
    const r = await fetch(`${API}/admin/restaurante/config?logo_tipo=${lq}`, { method: "PUT", headers: jsonHeaders, body: JSON.stringify({ categoria_menu: cat }) });
    if (r.ok) { toast.success("Categoría guardada"); buscar(); } else toast.error("No se pudo guardar");
  };
  const agregar = async (pid) => {
    const r = await fetch(`${API}/admin/restaurante/menu?logo_tipo=${lq}`, { method: "POST", headers: jsonHeaders, body: JSON.stringify({ producto_id: pid }) });
    if (r.ok) { fetchMenu(); } else toast.error((await r.json().catch(() => ({}))).detail || "No se pudo agregar");
  };
  const quitar = async (m) => {
    if (!window.confirm(`¿Quitar «${m.nombre}» del menú de cocina?`)) return;
    const r = await fetch(`${API}/admin/restaurante/menu/${m.id}?logo_tipo=${lq}`, { method: "DELETE", headers });
    if (r.ok) fetchMenu(); else toast.error("No se pudo quitar");
  };
  const toggleCocina = async (m) => {
    const r = await fetch(`${API}/admin/restaurante/menu/${m.id}?logo_tipo=${lq}`, { method: "PUT", headers: jsonHeaders, body: JSON.stringify({ va_a_cocina: m.va_a_cocina === false }) });
    if (r.ok) fetchMenu(); else toast.error("No se pudo actualizar");
  };

  return (
    <div className="space-y-6">
      <div className="border border-white/10 rounded-xl p-4 bg-white/3">
        <h3 className="text-white font-semibold font-body mb-1">Categoría del menú</h3>
        <p className="text-slate-500 text-xs mb-3 font-body">Los platos del menú se toman de esta categoría del catálogo. Por defecto «Servicios», que no lleva stock.</p>
        <div className="flex gap-2 max-w-md">
          <input list="rest-cats" value={cat} onChange={e => setCat(e.target.value)} className={INPUT} />
          <datalist id="rest-cats">{cats.map(c => <option key={c} value={c} />)}</datalist>
          <button onClick={guardarCat} className={BTN_PRI}>Guardar</button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-white/10 rounded-xl p-4 bg-white/3">
          <h3 className="text-white font-semibold font-body mb-3">Agregar productos al menú</h3>
          <input className={`${INPUT} mb-2`} placeholder="Buscar por nombre o SKU…" value={q} onChange={e => setQ(e.target.value)} />
          <label className="flex items-center gap-2 text-xs text-slate-400 mb-2"><input type="checkbox" checked={soloCat} onChange={e => setSoloCat(e.target.checked)} /> Solo la categoría «{cat}»</label>
          <div className="max-h-80 overflow-y-auto divide-y divide-white/5 border border-white/10 rounded-lg">
            {cands.length === 0 && <div className="p-3 text-sm text-slate-500">Sin productos para agregar</div>}
            {cands.map(p => (
              <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-slate-200">{p.nombre} <span className="text-xs text-slate-500">{p.categoria}</span></span>
                <button onClick={() => agregar(p.id)} className="text-orange-300 hover:text-orange-200 p-1"><Plus className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 rounded-xl p-4 bg-white/3">
          <h3 className="text-white font-semibold font-body mb-3">Menú de cocina ({menu.length})</h3>
          <div className="max-h-96 overflow-y-auto divide-y divide-white/5 border border-white/10 rounded-lg">
            {menu.length === 0 && <div className="p-3 text-sm text-slate-500">Todavía no hay platos</div>}
            {menu.map(m => (
              <div key={m.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="text-slate-200">{m.nombre}</span>
                <span className="flex items-center gap-3 shrink-0">
                  <label className="flex items-center gap-1 text-xs text-slate-400"><input type="checkbox" checked={m.va_a_cocina !== false} onChange={() => toggleCocina(m)} /> va a cocina</label>
                  <button onClick={() => quitar(m)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RestaurantePage() {
  const { token, hasPermission, activeEmpresaPropia, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { ...headers, "Content-Type": "application/json" };
  const logoTipo = activeEmpresaPropia?.slug || "arandujar";
  const isAdmin = user?.role === "admin" || user?.role === "gerente";

  const canVer = isAdmin || hasPermission("restaurante.ver");
  const canCrear = isAdmin || hasPermission("restaurante.crear");
  const canEditar = isAdmin || hasPermission("restaurante.editar");
  const canEliminar = isAdmin || hasPermission("restaurante.eliminar");
  const canCerrar = isAdmin || hasPermission("restaurante.cerrar_cuenta");
  const canGenerarTarjetas = isAdmin || hasPermission("restaurante.generar_tarjetas");
  const canLiberarSinCobrar = isAdmin || hasPermission("restaurante.liberar_sin_cobrar");
  const canCocina = isAdmin || hasPermission("restaurante.cocina");
  const canMozo = isAdmin || hasPermission("restaurante.mozo");
  const canDisp = isAdmin || hasPermission("restaurante.disponibilidad");
  const canConfig = isAdmin || hasPermission("restaurante.configurar");
  const esKiosco = !!user?.modulo_unico;

  const TABS = [
    canVer && ["salon", "Salón", UtensilsCrossed],
    canCocina && ["cocina", "Cocina", ChefHat],
    canMozo && ["mozos", "Mozos", ConciergeBell],
    (canDisp || canConfig) && ["disponibilidad", "Disponibilidad", Ban],
    canVer && ["tarjetas", "Tarjetas", CreditCard],
    canConfig && ["config", "Configuración", Settings],
  ].filter(Boolean);
  const [tab, setTab] = useState(TABS[0]?.[0] || "salon");
  const [menu, setMenu] = useState([]);
  const menuMap = useMemo(() => Object.fromEntries(menu.map(m => [m.producto_id, m])), [menu]);
  const fetchMenu = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/restaurante/menu?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
      if (r.ok) setMenu(await r.json());
    } catch (e) { /* silencioso */ }
  }, [logoTipo, token]); // eslint-disable-line
  useEffect(() => { if (TABS.length) fetchMenu(); }, [fetchMenu]); // eslint-disable-line
  const [tarjetas, setTarjetas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  // ── Salón ──────────────────────────────────────────────
  const [codigoTarjetaInput, setCodigoTarjetaInput] = useState("");
  const [comandaActiva, setComandaActiva] = useState(null);
  const [productoSelId, setProductoSelId] = useState("");
  const [barcodeProducto, setBarcodeProducto] = useState("");
  const [cantidadInput, setCantidadInput] = useState("1");
  const tarjetaInputRef = useRef(null);
  const productoBarcodeRef = useRef(null);

  const fetchTarjetas = async () => {
    if (!canVer) { setTarjetas([]); return; }
    try {
      const r = await fetch(`${API}/admin/restaurante/tarjetas?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "No se pudo cargar el listado de tarjetas");
        setTarjetas([]);
        return;
      }
      setTarjetas(await r.json());
    } catch (e) {
      toast.error("Error al cargar el listado de tarjetas");
      setTarjetas([]);
    }
  };

  const fetchProductos = async () => {
    try {
      const r = await fetch(`${API}/admin/productos?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
      if (r.ok) { const d = await r.json(); setProductos(Array.isArray(d) ? d : (d.items || [])); }
    } catch (e) { /* silencioso */ }
  };

  useEffect(() => { fetchTarjetas(); if (canVer) fetchProductos(); }, [logoTipo]); // eslint-disable-line

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchTarjetas(), canVer ? fetchProductos() : null, fetchMenu()]);
    setRefreshing(false);
  };

  const buscarTarjeta = async () => {
    const code = codigoTarjetaInput.trim();
    if (!code) return;
    try {
      const r = await fetch(`${API}/admin/restaurante/comandas/by-barcode/${encodeURIComponent(code)}?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
      if (!r.ok) {
        toast.error("No se encontró ninguna tarjeta con ese código o número");
        return;
      }
      const data = await r.json();
      setComandaActiva(data);
      setCodigoTarjetaInput("");
      setTimeout(() => productoBarcodeRef.current?.focus(), 50);
    } catch (e) {
      toast.error("Error al buscar la tarjeta");
    }
  };

  const refrescarComandaActiva = async () => {
    if (!comandaActiva) return;
    const r = await fetch(`${API}/admin/restaurante/comandas/by-barcode/${encodeURIComponent(comandaActiva.codigo_barras)}?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
    if (r.ok) setComandaActiva(await r.json());
  };

  const agregarProductoAComanda = async (prod, variante = null) => {
    if (!comandaActiva) return;
    if (menuMap[prod.id]?.disponible === false) {
      toast.error(`«${prod.nombre || prod.descripcion}» no está disponible hoy (agotado en cocina)`);
      setProductoSelId(""); setBarcodeProducto("");
      return;
    }
    const cantidad = Math.max(1, Number(cantidadInput || 1) || 1);
    const precio = variante?.precio_venta ?? prod.precio_venta ?? prod.precio ?? 0;
    try {
      const r = await fetch(`${API}/admin/restaurante/comandas/${comandaActiva.id}/items`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({
          producto_id: prod.id,
          codigo_barras: prod.codigo_barras || "",
          nombre: `${prod.nombre || prod.descripcion}${variante?.atributos_texto ? ` (${variante.atributos_texto})` : ""}`,
          cantidad,
          precio_unitario: precio,
          moneda_producto: prod.moneda && prod.moneda !== "PYG" ? prod.moneda : null,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "No se pudo agregar el ítem");
      setComandaActiva(await r.json());
      toast.success(`${prod.nombre || prod.descripcion} agregado a la Mesa N° ${comandaActiva.numero}`);
      setCantidadInput("1");
      setProductoSelId("");
    } catch (e) {
      toast.error(e.message || "No se pudo agregar el ítem");
    }
  };

  const escanearProducto = async () => {
    const code = barcodeProducto.trim();
    if (!code || !comandaActiva) return;
    let prod = productos.find(p => (p.codigo_barras || "").trim() === code);
    if (!prod) {
      try {
        const r = await fetch(`${API}/admin/productos/by-barcode/${encodeURIComponent(code)}?logo_tipo=${encodeURIComponent(logoTipo)}`, { headers });
        if (r.ok) prod = await r.json();
      } catch (e) { /* sigue con prod undefined */ }
    }
    if (!prod) {
      toast.error("No se encontró producto con ese código de barras");
      setBarcodeProducto("");
      return;
    }
    await agregarProductoAComanda(prod);
    setBarcodeProducto("");
    productoBarcodeRef.current?.focus();
  };

  const quitarItem = async (itemId) => {
    if (!comandaActiva) return;
    const r = await fetch(`${API}/admin/restaurante/comandas/${comandaActiva.id}/items/${itemId}`, {
      method: "DELETE", headers,
    });
    if (r.ok) { setComandaActiva(await r.json()); toast.success("Ítem quitado"); }
  };

  const enviarACocina = async () => {
    if (!comandaActiva) return;
    try {
      const r = await fetch(`${API}/admin/restaurante/comandas/${comandaActiva.id}/enviar-cocina`, { method: "POST", headers });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "No se pudo enviar a cocina");
      setComandaActiva(d);
      toast.success(`Pedido enviado a cocina — Mesa N° ${comandaActiva.numero}`);
    } catch (e) { toast.error(e.message); }
  };

  const liberarTarjetaActiva = async () => {
    if (!comandaActiva) return;
    if (!window.confirm(`¿Liberar la Mesa N° ${comandaActiva.numero} sin cobrar? Se perderá el consumo cargado.`)) return;
    const r = await fetch(`${API}/admin/restaurante/comandas/${comandaActiva.id}/liberar?sin_cobrar=true`, { method: "POST", headers });
    if (r.ok) {
      toast.success(`Mesa N° ${comandaActiva.numero} liberada`);
      setComandaActiva(null);
      fetchTarjetas();
    } else {
      toast.error((await r.json().catch(() => ({}))).detail || "No se pudo liberar la tarjeta");
    }
  };

  // ── Tarjetas ───────────────────────────────────────────
  const [rangoDesde, setRangoDesde] = useState("1");
  const [rangoHasta, setRangoHasta] = useState("20");
  const [cargaCodigo, setCargaCodigo] = useState("");
  const [cargaNumero, setCargaNumero] = useState("");
  const [cargaMesa, setCargaMesa] = useState("");
  const [savingTarjetas, setSavingTarjetas] = useState(false);

  const generarLote = async () => {
    setSavingTarjetas(true);
    try {
      const r = await fetch(`${API}/admin/restaurante/tarjetas/generar`, {
        method: "POST", headers: jsonHeaders,
        body: JSON.stringify({ logo_tipo: logoTipo, desde: Number(rangoDesde), hasta: Number(rangoHasta) }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "No se pudo generar el lote");
      const data = await r.json();
      toast.success(`${data.creadas} tarjeta(s) generadas`);
      fetchTarjetas();
    } catch (e) {
      toast.error(e.message || "No se pudo generar el lote");
    } finally {
      setSavingTarjetas(false);
    }
  };

  const cargarExistente = async () => {
    if (!cargaCodigo.trim()) { toast.error("Ingresá el código de barras de la tarjeta física"); return; }
    setSavingTarjetas(true);
    try {
      const r = await fetch(`${API}/admin/restaurante/tarjetas/cargar`, {
        method: "POST", headers: jsonHeaders,
        body: JSON.stringify({ logo_tipo: logoTipo, codigo_barras: cargaCodigo.trim(), numero: cargaNumero.trim() || null, mesa_nombre: cargaMesa.trim() || null }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "No se pudo cargar la tarjeta");
      toast.success("Tarjeta cargada correctamente");
      setCargaCodigo(""); setCargaNumero(""); setCargaMesa("");
      fetchTarjetas();
    } catch (e) {
      toast.error(e.message || "No se pudo cargar la tarjeta");
    } finally {
      setSavingTarjetas(false);
    }
  };

  const eliminarTarjeta = async (t) => {
    if (!window.confirm(`¿Eliminar la tarjeta N° ${t.numero}?`)) return;
    const r = await fetch(`${API}/admin/restaurante/tarjetas/${t.id}`, { method: "DELETE", headers });
    if (r.ok) { toast.success("Tarjeta eliminada"); fetchTarjetas(); }
    else toast.error((await r.json().catch(() => ({}))).detail || "No se pudo eliminar");
  };

  if (TABS.length === 0) {
    return (
      <div className="p-8 text-center text-slate-400 font-body">
        No tenés permiso para ver el módulo Restaurante.
      </div>
    );
  }

  return (
    <div className={`p-6 mx-auto ${["cocina", "mozos"].includes(tab) ? "max-w-none" : "max-w-6xl"}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {!esKiosco && (
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/5">
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <UtensilsCrossed className="w-6 h-6 text-orange-400" />
          <h1 className="text-xl font-bold text-white font-body">Restaurante</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={handleRefresh} disabled={refreshing} className={BTN_SEC}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Actualizar
          </button>
          {esKiosco && (
            <button onClick={() => logout()} className={BTN_SEC} title="Cerrar sesión">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Salir</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-6">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-sm font-body font-medium border-b-2 transition-all -mb-px ${
              tab === id ? "text-orange-300 border-orange-400" : "text-slate-400 border-transparent hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "salon" && (
        <div className="space-y-4">
          {!comandaActiva ? (
            <div className="border border-white/10 rounded-xl p-6 bg-white/3 text-center">
              <CreditCard className="w-8 h-8 text-orange-400 mx-auto mb-3" />
              <p className="text-slate-300 font-body mb-3">Escaneá o ingresá el número de la tarjeta/mesa para abrir la comanda</p>
              <div className="flex gap-2 max-w-sm mx-auto">
                <input
                  ref={tarjetaInputRef}
                  autoFocus
                  value={codigoTarjetaInput}
                  onChange={e => setCodigoTarjetaInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") buscarTarjeta(); }}
                  placeholder="N° de mesa o código de barras"
                  className={INPUT}
                />
                <button onClick={buscarTarjeta} className={BTN_PRI}><Search className="w-4 h-4" /> Abrir</button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between border border-orange-500/30 bg-orange-500/10 rounded-xl px-4 py-3">
                <div>
                  <span className="text-white font-semibold font-body">Mesa / Tarjeta N° {comandaActiva.numero}</span>
                  {comandaActiva.mesa_nombre && <span className="text-slate-400 text-sm ml-2">({comandaActiva.mesa_nombre})</span>}
                </div>
                <div className="flex gap-2">
                  {canLiberarSinCobrar && (
                    <button onClick={liberarTarjetaActiva} className={BTN_SEC}>
                      <Unlock className="w-4 h-4" /> Liberar sin cobrar
                    </button>
                  )}
                  <button onClick={() => setComandaActiva(null)} className={BTN_SEC}><X className="w-4 h-4" /> Cerrar</button>
                </div>
              </div>

              {canCrear && (
                <div className="border border-white/10 rounded-xl p-4 bg-white/3 space-y-3">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className={LABEL}>Escanear producto</label>
                      <input
                        ref={productoBarcodeRef}
                        autoFocus
                        value={barcodeProducto}
                        onChange={e => setBarcodeProducto(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") escanearProducto(); }}
                        placeholder="Código de barras del producto"
                        className={INPUT}
                      />
                    </div>
                    <div className="w-20">
                      <label className={LABEL}>Cant.</label>
                      <input value={cantidadInput} onChange={e => setCantidadInput(e.target.value)} className={INPUT} />
                    </div>
                  </div>
                  <div>
                    <label className={LABEL}>o buscar en el catálogo</label>
                    <ProductoSearchSelect
                      productos={productos}
                      value={productoSelId}
                      onChange={(id, prod, variante) => { if (prod) agregarProductoAComanda(prod, variante); }}
                      placeholder="Buscar producto por nombre o SKU..."
                      theme="dark"
                    />
                  </div>
                </div>
              )}

              {canCrear && (comandaActiva.items || []).some(it => it.va_a_cocina && !it.pedido_id) && (
                <button onClick={enviarACocina} className={`${BTN_PRI} w-full justify-center !py-3 !text-base`}>
                  <Send className="w-5 h-5" /> Enviar a cocina ({(comandaActiva.items || []).filter(it => it.va_a_cocina && !it.pedido_id).length} ítem/s nuevos)
                </button>
              )}
              <div className="border border-white/10 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-slate-400 text-xs uppercase font-body">
                    <tr>
                      <th className="text-left px-4 py-2.5">Producto / cocina</th>
                      <th className="text-right px-4 py-2.5">Cant.</th>
                      <th className="text-right px-4 py-2.5">Precio</th>
                      <th className="text-right px-4 py-2.5">Subtotal</th>
                      <th className="text-right px-4 py-2.5">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {(comandaActiva.items || []).length === 0 && (
                      <tr><td colSpan={5} className="text-center text-slate-500 py-6 font-body">Sin consumo cargado todavía.</td></tr>
                    )}
                    {(comandaActiva.items || []).map(it => (
                      <tr key={it.id} className="hover:bg-white/5">
                        <td className="px-4 py-2.5 text-white">{it.nombre}
                          {it.va_a_cocina && (() => {
                            const ped = (comandaActiva.pedidos || []).find(pp => pp.id === it.pedido_id);
                            return ped
                              ? <span className={`ml-2 text-xs px-2 py-0.5 rounded-full border ${COLOR_ESTADO[ped.estado].badge}`}>Pedido #{ped.numero} · {LABEL_ESTADO[ped.estado]}</span>
                              : <span className="ml-2 text-xs px-2 py-0.5 rounded-full border border-slate-500/40 text-slate-400">Sin enviar</span>;
                          })()}
                        </td>
                        <td className="px-4 py-2.5 text-right text-slate-300">{it.cantidad}</td>
                        <td className="px-4 py-2.5 text-right text-slate-300">{fmtGs(it.precio_unitario)}</td>
                        <td className="px-4 py-2.5 text-right text-white font-medium">{fmtGs(it.subtotal)}</td>
                        <td className="px-4 py-2.5 text-right">
                          {canEditar && (
                            <button onClick={() => quitarItem(it.id)} className="text-slate-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-white/10">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {(comandaActiva.items || []).length > 0 && (
                    <tfoot>
                      <tr className="bg-white/5">
                        <td colSpan={3} className="px-4 py-2.5 text-right text-slate-400 font-body">Total consumo</td>
                        <td className="px-4 py-2.5 text-right text-white font-bold">{fmtGs(comandaActiva.total_consumo)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <p className="text-slate-500 text-xs font-body">
                Para cobrar: en PDV o en Facturas, escaneá esta misma tarjeta en el campo de código de barras — el consumo se
                carga automáticamente al ticket o a la factura, y la tarjeta se libera sola al confirmar la venta.
              </p>
            </div>
          )}
        </div>
      )}

      {tab === "cocina" && canCocina && <PedidosGrid vista="cocina" API={API} headers={headers} jsonHeaders={jsonHeaders} logoTipo={logoTipo} />}
      {tab === "mozos" && canMozo && <PedidosGrid vista="mozos" API={API} headers={headers} jsonHeaders={jsonHeaders} logoTipo={logoTipo} />}
      {tab === "disponibilidad" && (canDisp || canConfig) && <Disponibilidad API={API} headers={headers} jsonHeaders={jsonHeaders} logoTipo={logoTipo} menu={menu} fetchMenu={fetchMenu} canDisp={canDisp || canConfig} />}
      {tab === "config" && canConfig && <ConfigMenu API={API} headers={headers} jsonHeaders={jsonHeaders} logoTipo={logoTipo} menu={menu} fetchMenu={fetchMenu} />}

      {tab === "tarjetas" && (
        <div className="space-y-6">
          {canGenerarTarjetas && (
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-white/10 rounded-xl p-4 bg-white/3">
                <h3 className="text-white font-semibold font-body mb-3">Generar tarjetas en lote</h3>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className={LABEL}>Desde</label>
                    <input value={rangoDesde} onChange={e => setRangoDesde(e.target.value)} className={INPUT} />
                  </div>
                  <div className="flex-1">
                    <label className={LABEL}>Hasta</label>
                    <input value={rangoHasta} onChange={e => setRangoHasta(e.target.value)} className={INPUT} />
                  </div>
                  <button onClick={generarLote} disabled={savingTarjetas} className={BTN_PRI}>
                    <Plus className="w-4 h-4" /> Generar
                  </button>
                </div>
                <p className="text-slate-500 text-xs mt-2 font-body">El sistema genera el código de barras de cada tarjeta automáticamente.</p>
              </div>

              <div className="border border-white/10 rounded-xl p-4 bg-white/3">
                <h3 className="text-white font-semibold font-body mb-3">Cargar tarjeta física existente</h3>
                <div className="space-y-2">
                  <input value={cargaCodigo} onChange={e => setCargaCodigo(e.target.value)} placeholder="Código de barras impreso" className={INPUT} />
                  <div className="flex gap-2">
                    <input value={cargaNumero} onChange={e => setCargaNumero(e.target.value)} placeholder="N° (opcional)" className={INPUT} />
                    <input value={cargaMesa} onChange={e => setCargaMesa(e.target.value)} placeholder="Mesa (opcional)" className={INPUT} />
                  </div>
                  <button onClick={cargarExistente} disabled={savingTarjetas} className={BTN_PRI}>
                    <Plus className="w-4 h-4" /> Cargar tarjeta
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-slate-400 text-xs uppercase font-body">
                <tr>
                  <th className="text-left px-4 py-2.5">N°</th>
                  <th className="text-left px-4 py-2.5">Código de barras</th>
                  <th className="text-left px-4 py-2.5">Mesa</th>
                  <th className="text-left px-4 py-2.5">Estado</th>
                  <th className="text-right px-4 py-2.5">Consumo</th>
                  <th className="text-right px-4 py-2.5">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {tarjetas.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-500 py-8 font-body">Sin tarjetas registradas.</td></tr>
                )}
                {tarjetas.map(t => (
                  <tr key={t.id} className="hover:bg-white/5">
                    <td className="px-4 py-2.5 text-white font-mono">{t.numero}</td>
                    <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{t.codigo_barras}</td>
                    <td className="px-4 py-2.5 text-slate-300">{t.mesa_nombre || "-"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-1 rounded-full border ${
                        t.estado === "ocupada" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                      }`}>
                        {t.estado === "ocupada" ? "Ocupada" : "Libre"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-300">{fmtGs(t.total_consumo)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => imprimirTarjeta(t, activeEmpresaPropia)} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10" title="Imprimir">
                          <Printer className="w-4 h-4" />
                        </button>
                        {canEliminar && t.estado === "libre" && (
                          <button onClick={() => eliminarTarjeta(t)} className="text-slate-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-white/10" title="Eliminar">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
